import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, startImpersonation } from "@/lib/auth";
import { UserPlus, Loader2, KeyRound, Eye, Ban, Trash2, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";

export const ElevateModal = ({ onDone, onClose }) => {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const verify = async () => {
        setBusy(true);
        try {
            await api.post("/admin/elevate/verify", { code });
            toast.success("Verified — you're elevated for 15 minutes.");
            await onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid code.");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-4" data-testid="elevate-modal">
            <div className="bg-[#1B2E55] border border-[#FF5A00]/40 p-8 max-w-sm w-full">
                <div className="flex items-center gap-2 mb-3"><KeyRound size={15} className="text-[#FF5A00]" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Step-up verification</span></div>
                <p className="text-sm text-slate-300">This action needs a fresh security code. We emailed one to you — enter it below.</p>
                <input autoFocus value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="6-digit code"
                    data-testid="elevate-code-input"
                    className="mt-4 w-full bg-[#0F1E3B] border border-white/10 text-white px-3 py-2.5 text-sm font-mono-tech tracking-[0.4em] text-center" />
                <div className="mt-4 flex gap-2">
                    <button onClick={verify} disabled={busy || code.length !== 6} data-testid="elevate-verify-btn"
                        className="flex-1 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white py-2.5 text-xs font-semibold uppercase tracking-widest">
                        {busy ? <Loader2 size={13} className="animate-spin mx-auto" /> : "Verify"}
                    </button>
                    <button onClick={onClose} className="px-4 border border-white/15 text-slate-400 hover:text-white text-xs uppercase tracking-widest">Cancel</button>
                </div>
            </div>
        </div>
    );
};

// Wraps sensitive calls: on 428, opens the step-up modal then retries.
export const useElevation = () => {
    const [open, setOpen] = useState(false);
    const [after, setAfter] = useState(null);
    const withElevation = useCallback(async (fn) => {
        try { await fn(); } catch (e) {
            if (e.response?.status === 428) {
                await api.post("/admin/elevate");
                toast.info("Security code sent to your email.");
                setAfter(() => fn);
                setOpen(true);
            } else {
                toast.error(e.response?.data?.detail || "Action failed.");
            }
        }
    }, []);
    const elevateUI = open ? (
        <ElevateModal
            onDone={async () => { setOpen(false); if (after) { await after(); setAfter(null); } }}
            onClose={() => setOpen(false)}
        />
    ) : null;
    return { withElevation, elevateUI };
};

export const StaffPanel = ({ can, withElevation, meId }) => {
    const [staff, setStaff] = useState(null);
    const [roles, setRoles] = useState([]);
    const [perms, setPerms] = useState([]);
    const [form, setForm] = useState({ name: "", email: "", role: "sales" });
    const [creating, setCreating] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const load = () => api.get("/admin/staff").then(({ data }) => setStaff(data.staff)).catch(() => setStaff([]));
    useEffect(() => {
        load();
        api.get("/admin/roles").then(({ data }) => setRoles(data.roles));
        api.get("/admin/permissions").then(({ data }) => setPerms(data.permissions));
    }, []);

    const create = async () => {
        if (!form.name || !form.email) return;
        setCreating(true);
        await withElevation(async () => {
            const { data } = await api.post("/admin/staff", form);
            toast.success(`Account created. Temp password: ${data.temp_password} (also emailed)`, { duration: 15000 });
            setForm({ name: "", email: "", role: "sales" });
            load();
        });
        setCreating(false);
    };

    if (!can("staff.manage")) return <div className="text-sm text-slate-500" data-testid="staff-no-access">You don't have permission to manage staff.</div>;
    return (
        <div className="space-y-8">
            <div className="border border-white/10 p-5 flex flex-wrap items-end gap-3" data-testid="staff-create-form">
                <div><label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mb-1">Name</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input"
                        className="bg-[#1B2E55] border border-white/10 px-3 py-2 text-sm text-white w-44" /></div>
                <div><label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mb-1">Email</label>
                    <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input"
                        className="bg-[#1B2E55] border border-white/10 px-3 py-2 text-sm text-white w-60" /></div>
                <div><label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mb-1">Role</label>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role-select"
                        className="bg-[#1B2E55] border border-white/10 px-3 py-2 text-sm text-white">
                        {roles.filter((r) => r.key !== "super_admin").map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    </select></div>
                <button onClick={create} disabled={creating || !form.name || !form.email} data-testid="staff-create-btn"
                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                    <UserPlus size={13} /> Create account
                </button>
                <span className="text-[10px] text-slate-500">A strong password is auto-generated and emailed to them.</span>
            </div>

            {!staff ? <Loader2 className="animate-spin text-[#FF5A00]" size={18} /> : (
                <div className="border border-white/10 divide-y divide-white/5" data-testid="staff-list">
                    {staff.map((s) => (
                        <div key={s.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`staff-row-${s.email}`}>
                            <div className="flex-1 min-w-[180px]">
                                <div className="text-sm text-white">{s.name || s.email} {s.id === meId && <span className="text-[#FF5A00] text-[10px]">(you)</span>}</div>
                                <div className="text-[10px] font-mono-tech text-slate-500">{s.email}</div>
                            </div>
                            <select value={s.role} data-testid={`staff-role-${s.email}`}
                                disabled={s.role === "super_admin"}
                                onChange={(e) => withElevation(async () => {
                                    await api.patch(`/admin/staff/${s.id}`, { role: e.target.value });
                                    toast.success("Role updated."); load();
                                })}
                                className="bg-[#1B2E55] border border-white/10 px-2 py-1.5 text-xs text-white">
                                {s.role === "super_admin" && <option value="super_admin">Super Admin</option>}
                                {roles.filter((r) => r.key !== "super_admin").map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                            </select>
                            <span className={`px-2 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border ${s.active === false ? "border-red-400/50 text-red-300" : "border-emerald-400/40 text-emerald-300"}`}>
                                {s.active === false ? "suspended" : "active"}
                            </span>
                            {s.role !== "super_admin" && (
                                <>
                                    <button title={s.active === false ? "Reactivate" : "Suspend"} data-testid={`staff-suspend-${s.email}`}
                                        onClick={() => withElevation(async () => {
                                            await api.patch(`/admin/staff/${s.id}`, { active: s.active === false });
                                            toast.success(s.active === false ? "Reactivated." : "Suspended."); load();
                                        })}
                                        className="text-slate-400 hover:text-amber-300"><Ban size={14} /></button>
                                    <button title="Delete" data-testid={`staff-delete-${s.email}`}
                                        onClick={() => withElevation(async () => {
                                            await api.delete(`/admin/staff/${s.id}`);
                                            toast.success("Account deleted."); load();
                                        })}
                                        className="text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                                </>
                            )}
                            {can("impersonate") && s.id !== meId && s.role !== "super_admin" && (
                                <button title="Impersonate (logged)" data-testid={`staff-impersonate-${s.email}`}
                                    onClick={() => withElevation(async () => {
                                        const { data } = await api.post(`/admin/impersonate/${s.id}`);
                                        startImpersonation(data.access_token, data.user);
                                    })}
                                    className="text-slate-400 hover:text-[#FF5A00]"><Eye size={14} /></button>
                            )}
                            {s.role !== "super_admin" && (
                                <button title="Permissions" data-testid={`staff-perms-toggle-${s.email}`}
                                    onClick={() => setExpanded((x) => (x === s.id ? null : s.id))}
                                    className="text-slate-400 hover:text-[#FF5A00] inline-flex items-center gap-1">
                                    <SlidersHorizontal size={13} />
                                    {expanded === s.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {staff && staff.filter((s) => s.id === expanded).map((s) => (
                <PermissionsEditor key={s.id} staffMember={s} roles={roles} perms={perms}
                    withElevation={withElevation} onSaved={load} />
            ))}
        </div>
    );
};

const PermissionsEditor = ({ staffMember: s, roles, perms, withElevation, onSaved }) => {
    const role = roles.find((r) => r.key === s.role);
    const basePerms = role?.permissions || [];
    const grants = s.perm_grants || [];
    const revokes = s.perm_revokes || [];
    const isChecked = (key) => (basePerms.includes(key) ? !revokes.includes(key) : grants.includes(key));

    const toggle = async (key, checked) => {
        let newGrants = grants, newRevokes = revokes;
        if (checked) {
            newGrants = basePerms.includes(key) ? grants : [...new Set([...grants, key])];
            newRevokes = revokes.filter((p) => p !== key);
        } else {
            newRevokes = basePerms.includes(key) ? [...new Set([...revokes, key])] : revokes;
            newGrants = basePerms.includes(key) ? grants : grants.filter((p) => p !== key);
        }
        await withElevation(async () => {
            await api.patch(`/admin/staff/${s.id}`, { perm_grants: newGrants, perm_revokes: newRevokes });
            toast.success("Permissions updated.");
            onSaved();
        });
    };

    return (
        <div className="border border-[#FF5A00]/30 bg-[#1B2E55]/40 p-4" data-testid={`staff-perms-editor-${s.email}`}>
            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-3">
                / Individual permission overrides for {s.name || s.email} — base role adds what&apos;s checked by default
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid={`staff-perm-row-${s.email}-${p.key}`}>
                        <input type="checkbox" checked={isChecked(p.key)} onChange={(e) => toggle(p.key, e.target.checked)}
                            data-testid={`staff-perm-checkbox-${s.email}-${p.key}`} />
                        <span className={isChecked(p.key) !== basePerms.includes(p.key) ? "text-[#FF5A00]" : ""}>{p.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );
};
